import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

interface InspirationPin {
  id: string;
  pinType: 'image' | 'link' | 'playlist' | 'quote' | 'note' | 'color';
  content: string;
  title: string | null;
  caption: string | null;
  emoji: string | null;
  boardName: string | null;
  mood: string | null;
  sortOrder: number;
  favorite: boolean;
}

interface Board {
  boardName: string;
  count: number;
}

interface InspirationBoardPanelProps {
  companionId: string;
  onClose: () => void;
}

const PIN_TYPES = [
  { value: 'image', label: '🖼️ Image', emoji: '🖼️' },
  { value: 'link', label: '🔗 Link', emoji: '🔗' },
  { value: 'playlist', label: '🎵 Playlist', emoji: '🎵' },
  { value: 'quote', label: '✍️ Quote', emoji: '✍️' },
  { value: 'note', label: '📝 Note', emoji: '📝' },
  { value: 'color', label: '🎨 Color', emoji: '🎨' },
];

const MOODS = [
  '🕊️ sacred',
  '✨ playful',
  '🌧️ melancholy',
  '🔥 passionate',
  '🌙 dreamy',
  '💜 tender',
  '⚡ electric',
  '🍃 grounded',
  '🌊 flowing',
  '🗡️ fierce',
];

export default function InspirationBoardPanel({
  companionId,
  onClose,
}: InspirationBoardPanelProps) {
  const { t } = useTranslation();
  const [pins, setPins] = useState<InspirationPin[]>([]);
  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedBoard, setSelectedBoard] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');

  // Add form
  const [newType, setNewType] = useState<string>('image');
  const [newContent, setNewContent] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newCaption, setNewCaption] = useState('');
  const [newEmoji, setNewEmoji] = useState('');
  const [newBoard, setNewBoard] = useState('');
  const [newMood, setNewMood] = useState('');

  const fetchPins = useCallback(async () => {
    try {
      const params: Record<string, string> = { companionId };
      if (selectedBoard) params.boardName = selectedBoard;
      if (filterType) params.pinType = filterType;
      if (search) params.search = search;
      const res = await axios.get('/inspiration-boards/pins', { params });
      setPins(res.data);
    } catch (err) {
      console.error('Failed to fetch pins', err);
    } finally {
      setLoading(false);
    }
  }, [companionId, selectedBoard, filterType, search]);

  const fetchBoards = useCallback(async () => {
    try {
      const res = await axios.get('/inspiration-boards/boards', {
        params: { companionId },
      });
      setBoards(res.data);
    } catch (err) {
      console.error('Failed to fetch boards', err);
    }
  }, [companionId]);

  useEffect(() => {
    fetchPins();
    fetchBoards();
  }, [fetchPins, fetchBoards]);

  const handleAdd = async () => {
    if (!newContent.trim()) return;
    try {
      await axios.post('/inspiration-boards/pins', {
        companionId,
        pinType: newType,
        content: newContent.trim(),
        title: newTitle.trim() || undefined,
        caption: newCaption.trim() || undefined,
        emoji: newEmoji || undefined,
        boardName: newBoard.trim() || undefined,
        mood: newMood || undefined,
      });
      setNewContent('');
      setNewTitle('');
      setNewCaption('');
      setNewEmoji('');
      setNewBoard('');
      setNewMood('');
      setShowAdd(false);
      fetchPins();
      fetchBoards();
    } catch (err) {
      console.error('Failed to add pin', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`/inspiration-boards/pins/${id}`);
      fetchPins();
      fetchBoards();
    } catch (err) {
      console.error('Failed to delete pin', err);
    }
  };

  const handleToggleFavorite = async (pin: InspirationPin) => {
    try {
      await axios.patch(`/inspiration-boards/pins/${pin.id}`, {
        favorite: !pin.favorite,
      });
      fetchPins();
    } catch (err) {
      console.error('Failed to toggle favorite', err);
    }
  };

  const renderPin = (pin: InspirationPin) => {
    const typeInfo = PIN_TYPES.find((t) => t.value === pin.pinType);
    return (
      <div
        key={pin.id}
        className="bg-gray-800 rounded-lg p-3 group hover:bg-gray-750 transition-colors"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
              <span>{typeInfo?.emoji}</span>
              <span>{pin.pinType}</span>
              {pin.boardName && <span className="ml-1 text-purple-400">· {pin.boardName}</span>}
              {pin.mood && <span className="ml-1">{pin.mood}</span>}
            </div>

            {pin.title && (
              <p className="text-sm font-medium text-white mb-1">
                {pin.emoji && `${pin.emoji} `}
                {pin.title}
              </p>
            )}

            {pin.pinType === 'image' ? (
              <img
                src={pin.content}
                alt={pin.title || 'inspiration'}
                className="w-full max-h-48 object-cover rounded mb-1"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : pin.pinType === 'color' ? (
              <div className="w-full h-12 rounded mb-1" style={{ backgroundColor: pin.content }} />
            ) : pin.pinType === 'link' || pin.pinType === 'playlist' ? (
              <a
                href={pin.content}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-400 hover:text-blue-300 break-all"
              >
                {pin.content}
              </a>
            ) : (
              <p className="text-sm text-gray-200 whitespace-pre-wrap italic">{pin.content}</p>
            )}

            {pin.caption && <p className="text-xs text-gray-400 mt-1">{pin.caption}</p>}
          </div>

          <div className="flex flex-col items-center gap-1 opacity-60 group-hover:opacity-100">
            <button
              onClick={() => handleToggleFavorite(pin)}
              className={`text-sm ${pin.favorite ? 'text-yellow-400' : 'text-gray-500 hover:text-yellow-400'}`}
            >
              {pin.favorite ? '★' : '☆'}
            </button>
            <button
              onClick={() => handleDelete(pin.id)}
              className="text-sm text-gray-500 hover:text-red-400"
            >
              🗑
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex justify-end">
      <div className="w-full max-w-md bg-gray-900 h-full overflow-y-auto p-4 animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            🎨 {t('inspirationBoards', 'Inspiration Boards')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">
            ✕
          </button>
        </div>

        {/* Board chips */}
        {boards.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            <button
              onClick={() => setSelectedBoard('')}
              className={`px-2 py-1 rounded text-xs ${
                !selectedBoard
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              {t('allBoards', 'All')} ({pins.length})
            </button>
            {boards.map((b) => (
              <button
                key={b.boardName}
                onClick={() => setSelectedBoard(b.boardName)}
                className={`px-2 py-1 rounded text-xs ${
                  selectedBoard === b.boardName
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                {b.boardName} ({b.count})
              </button>
            ))}
          </div>
        )}

        {/* Search + Filter */}
        <div className="flex gap-2 mb-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('searchPins', 'Search pins...')}
            className="flex-1 bg-gray-800 text-white rounded px-3 py-2 text-sm border border-gray-700 focus:border-purple-500 outline-none"
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="bg-gray-800 text-white rounded px-2 py-2 text-sm border border-gray-700"
          >
            <option value="">{t('allTypes', 'All')}</option>
            {PIN_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Add Button */}
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="w-full mb-3 py-2 rounded bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium"
        >
          {showAdd ? t('cancel', 'Cancel') : `+ ${t('addPin', 'Add Pin')}`}
        </button>

        {/* Add Form */}
        {showAdd && (
          <div className="bg-gray-800 rounded p-3 mb-4 space-y-2">
            <div className="flex gap-2">
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value)}
                className="bg-gray-700 text-white rounded px-3 py-2 text-sm border border-gray-600"
              >
                {PIN_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <input
                value={newEmoji}
                onChange={(e) => setNewEmoji(e.target.value)}
                placeholder="✨"
                className="w-14 bg-gray-700 text-white rounded px-2 py-2 text-sm text-center border border-gray-600"
              />
            </div>
            {newType === 'quote' || newType === 'note' ? (
              <textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder={
                  newType === 'quote'
                    ? t('quoteText', '"The words themselves..."')
                    : t('noteText', 'Your note...')
                }
                className="w-full bg-gray-700 text-white rounded px-3 py-2 text-sm resize-none border border-gray-600 focus:border-purple-500 outline-none"
                rows={3}
              />
            ) : (
              <input
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder={
                  newType === 'color'
                    ? '#c9a96e'
                    : newType === 'image'
                      ? 'https://...'
                      : t('urlPlaceholder', 'URL...')
                }
                className="w-full bg-gray-700 text-white rounded px-3 py-2 text-sm border border-gray-600 focus:border-purple-500 outline-none"
              />
            )}
            <input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder={t('pinTitle', 'Title (optional)')}
              className="w-full bg-gray-700 text-white rounded px-3 py-2 text-sm border border-gray-600"
            />
            <input
              value={newCaption}
              onChange={(e) => setNewCaption(e.target.value)}
              placeholder={t('pinCaption', 'Caption (optional)')}
              className="w-full bg-gray-700 text-white rounded px-3 py-2 text-sm border border-gray-600"
            />
            <div className="flex gap-2">
              <input
                value={newBoard}
                onChange={(e) => setNewBoard(e.target.value)}
                placeholder={t('boardName', 'Board name...')}
                className="flex-1 bg-gray-700 text-white rounded px-3 py-2 text-sm border border-gray-600"
              />
              <select
                value={newMood}
                onChange={(e) => setNewMood(e.target.value)}
                className="bg-gray-700 text-white rounded px-2 py-2 text-sm border border-gray-600"
              >
                <option value="">{t('mood', 'Mood...')}</option>
                {MOODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={handleAdd}
              disabled={!newContent.trim()}
              className="w-full py-2 rounded bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white text-sm font-medium"
            >
              {t('savePin', 'Save Pin')}
            </button>
          </div>
        )}

        {/* Pin List */}
        {loading ? (
          <p className="text-gray-500 text-sm text-center py-8">{t('loading', 'Loading...')}</p>
        ) : pins.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">
              {t('noInspirationPins', 'No inspiration pins yet.')}
            </p>
            <p className="text-gray-600 text-xs mt-1">
              {t(
                'inspirationHint',
                'Pin images, links, playlists, quotes, notes, and colors to build mood boards for your worlds.',
              )}
            </p>
          </div>
        ) : (
          <div className="space-y-2">{pins.map(renderPin)}</div>
        )}
      </div>
    </div>
  );
}
